import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { twMerge } from "tailwind-merge";
import { useBreakpoint } from "@core/hooks/useBreakpoint";
import { Type } from "@themes/pollinator/ui/Type";
import triangle from "@themes/pollinator/assets/images/triangle.png";

export type Props = {
  /** Item title */
  title: string;
  /** Optional subheader */
  subheader?: string;
  /** Content text (supports markdown) */
  children: string;
  /** Whether the accordion is open by default */
  defaultOpen?: boolean;
  /** Whether to render as an accordion */
  isAccordion?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * A text item component that can optionally function as an accordion.
 * Supports markdown content.
 */
export const TextItem = ({
  title,
  subheader,
  children,
  defaultOpen = false,
  isAccordion = false,
  className,
}: Props) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isMdScreen = useBreakpoint("md");

  useEffect(() => {
    if (isMdScreen && isAccordion) {
      setIsOpen(true);
    }
  }, [isAccordion, isMdScreen]);

  const toggleAccordion = () => {
    if (isAccordion) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      className={twMerge("flex flex-col gap-0", className)}
      data-testid="text-item"
    >
      <div
        className={`flex flex-row gap-2 ${isAccordion ? "cursor-pointer" : ""}`}
        onClick={toggleAccordion}
        data-testid="text-item-header"
      >
        {isAccordion && (
          <img
            src={triangle}
            className={`relative top-1.5 w-[11px] h-[12px] transition-transform duration-200 md:w-[17px] md:h-[18px] ${
              isOpen ? "rotate-0" : "-rotate-90"
            }`}
            alt="triangle"
            data-testid="text-item-toggle"
          />
        )}
        <Type variant={"h4"} as="span">
          {title}
        </Type>
      </div>
      {(!isAccordion || isOpen) && (
        <div
          className="transition-all duration-300"
          data-testid="text-item-content"
        >
          <div className="flex flex-col gap-2">
            {subheader && (
              <Type variant="body2" as="span" className="whitespace-pre-line">
                {subheader}
              </Type>
            )}
            <Type variant="body2" as="div">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      className="underline text-brand-800 hover:text-brand-700"
                      data-testid="text-item-link"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {children}
              </ReactMarkdown>
            </Type>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextItem;

